import { useCallback, useEffect, useMemo } from 'react';
import { FileText, List, Loader2, Share2, Sparkles } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import PageShell from '@/components/PageShell';
import type { ViewMode } from '../actionVisuals';
import { computeFilteredStats, filterActionsByStatus } from '../filterActions';
import { useAgentActionReport } from '../hooks/useAgentActionReport';
import StatsCards from './StatsCards';
import ReportPanel from './ReportPanel';
import ActivityFeed from './ActivityFeed';
import { AppFilterSelect, StatusFilterSelect, TimeRangeSelect } from './AgentActionsFilters';
import ActionDetail from './ActionDetail';
import DependencyMap from './DependencyMap';
import { useAgentActionFeed } from '../hooks/useAgentActions';
import type { AgentAction, StatusFilter, TimeRange } from '../types';

const TABS: { id: ViewMode; label: string; Icon: typeof List }[] = [
  { id: 'feed', label: 'Activity', Icon: List },
  { id: 'detail', label: 'Detail', Icon: FileText },
  { id: 'map', label: 'Map', Icon: Share2 },
];

const STATUS_FILTERS: StatusFilter[] = [
  'all', 'failures', 'fixes', 'deploys', 'pending', 'agent_calls', 'data_sync', 'code_commits',
];

function tabButtonClass(active: boolean) {
  return active
    ? 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100'
    : 'bg-transparent border-gray-200/80 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200';
}

function parseStatusFilter(raw: string | null): StatusFilter {
  if (raw && STATUS_FILTERS.includes(raw as StatusFilter)) return raw as StatusFilter;
  return 'all';
}

export default function AgentActionsPage() {
  const [params, setParams] = useSearchParams();
  const view = (params.get('view') as ViewMode) || 'feed';
  const detailId = params.get('id');
  const appId = params.get('app_id');
  const range = (params.get('range') as TimeRange) || '24h';
  const statusFilter = parseStatusFilter(params.get('status'));

  const feed = useAgentActionFeed(appId, range);
  const report = useAgentActionReport(appId, range, statusFilter);

  const filteredActions = useMemo(
    () => filterActionsByStatus(feed.data?.actions || [], statusFilter),
    [feed.data?.actions, statusFilter],
  );

  const filteredStats = useMemo(
    () => computeFilteredStats(filteredActions),
    [filteredActions],
  );

  const syncParams = useCallback((next: Partial<{
    view: ViewMode;
    id: string | null;
    app_id: string | null;
    range: TimeRange;
    status: StatusFilter;
  }>, replace = false) => {
    const p = new URLSearchParams(params);
    if (next.view !== undefined) p.set('view', next.view);
    if (next.id !== undefined) {
      if (next.id) p.set('id', next.id);
      else p.delete('id');
    }
    if (next.app_id !== undefined) {
      if (next.app_id) p.set('app_id', next.app_id);
      else p.delete('app_id');
    }
    if (next.range !== undefined) p.set('range', next.range);
    if (next.status !== undefined) {
      if (next.status === 'all') p.delete('status');
      else p.set('status', next.status);
    }
    setParams(p, { replace });
  }, [params, setParams]);

  const setView = (next: ViewMode, id?: string | null) => {
    syncParams({ view: next, id: id ?? (next === 'detail' ? detailId : null) }, next === 'feed');
  };

  const handleSelectAction = (action: AgentAction) => {
    syncParams({ view: 'detail', id: action.id });
  };

  const activeTab = view === 'detail' ? 'detail' : view;

  const selectedAppName = useMemo(() => {
    if (!appId) return null;
    const fromFeed = feed.data?.actions?.find((a) => a.app_id === appId)?.app_name;
    return fromFeed || null;
  }, [appId, feed.data?.actions]);

  useEffect(() => {
    if (view === 'detail' && !detailId) {
      syncParams({ view: 'feed', id: null }, true);
    }
  }, [view, detailId, syncParams]);

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2 justify-end">
      <TimeRangeSelect
        value={range}
        onChange={(v) => syncParams({ range: v })}
      />
      <AppFilterSelect
        value={appId}
        onChange={(v) => syncParams({ app_id: v })}
      />
      <StatusFilterSelect
        value={statusFilter}
        onChange={(v) => syncParams({ status: v })}
      />
      <button
        type="button"
        disabled={report.loading}
        onClick={() => report.generate(Boolean(report.result))}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 whitespace-nowrap"
      >
        {report.loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
        Generate
      </button>
    </div>
  );

  return (
    <PageShell title="Agent Actions" subtitle="" actions={headerActions}>
      <ReportPanel
        open={report.open}
        loading={report.loading}
        error={report.error}
        result={report.result}
        agentRuns={filteredStats.agent_runs}
        onClose={report.close}
        onRegenerate={() => report.generate(true)}
      />

      <div className="flex flex-wrap gap-3 items-center pb-4 border-b border-gray-200/80 dark:border-gray-700 mb-6">
        <div className="flex gap-1">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                if (id === 'detail' && !detailId) return;
                setView(id);
              }}
              disabled={id === 'detail' && !detailId}
              className={`inline-flex items-center gap-1 border px-3 py-1.5 rounded-md text-[13px] transition-colors disabled:opacity-40 ${tabButtonClass(activeTab === id)}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'feed' && (
        <>
          <StatsCards stats={filteredStats} loading={feed.isLoading} />
          <ActivityFeed
            actions={filteredActions}
            loading={feed.isLoading}
            error={feed.isError}
            onSelect={handleSelectAction}
          />
        </>
      )}

      {view === 'detail' && detailId && (
        <ActionDetail actionId={detailId} onBack={() => setView('feed', null)} />
      )}

      {view === 'map' && (
        <DependencyMap
          appId={appId}
          appName={selectedAppName}
          onSelectApp={(id) => syncParams({ app_id: id, view: 'map' })}
        />
      )}
    </PageShell>
  );
}
